
import { MessageSquare, Loader2, ArrowRight } from "lucide-react";
import type { AgentOutput } from "../../gen/models";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  outputMeta?: AgentOutput;
}

interface InteractiveSandboxViewProps {
  chatMessages: ChatMessage[];
  chatInput: string;
  onChangeChatInput: (val: string) => void;
  onSubmit: (e?: React.FormEvent) => void;
  isSimulating: boolean;
}

export function InteractiveSandboxView({
  chatMessages,
  chatInput,
  onChangeChatInput,
  onSubmit,
  isSimulating,
}: InteractiveSandboxViewProps) {
  return (
    <div className="flex flex-col h-[520px] rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40 overflow-hidden">
      {/* Sandbox Messages Stream */}
      <div className="flex-1 p-4 overflow-y-auto space-y-4 [scrollbar-gutter:stable]">
        {chatMessages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6 text-stone-400 space-y-2">
            <MessageSquare className="w-8 h-8 text-stone-300 dark:text-stone-700" />
            <p className="text-xs">
              Start an interactive conversation with the candidate draft rules.
            </p>
            <span className="text-[10px] text-stone-400">
              Turns are stored with status='simulation' and will not trigger production evaluators.
            </span>
          </div>
        ) : (
          chatMessages.map((msg) => (
            <div
              key={msg.id}
              className={`flex flex-col ${
                msg.role === "user" ? "items-end" : "items-start"
              }`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-xs ${
                  msg.role === "user"
                    ? "bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 rounded-tr-xs"
                    : "bg-white dark:bg-stone-900 text-stone-800 dark:text-stone-100 border border-stone-200 dark:border-stone-800 rounded-tl-xs shadow-2xs"
                }`}
              >
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                {msg.outputMeta && (
                  <div className="mt-2 pt-1.5 border-t border-stone-100 dark:border-stone-800/80 flex flex-wrap items-center gap-2 text-[10px] text-stone-400 font-mono">
                    <span>{msg.outputMeta.latency_ms}ms</span>
                    <span>•</span>
                    <span>{msg.outputMeta.tokens} tok</span>
                    {(msg.outputMeta.rules_applied || []).length > 0 && (
                      <>
                        <span>•</span>
                        <span className="text-indigo-500 font-sans">
                          {(msg.outputMeta.rules_applied || []).length} rule(s)
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        {isSimulating && (
          <div className="flex items-center gap-2 text-xs text-stone-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>Silvestre agent drafting response with candidate rules...</span>
          </div>
        )}
      </div>

      {/* Chat Input Bar */}
      <form
        onSubmit={onSubmit}
        className="p-3 border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 flex items-center gap-2"
      >
        <input
          type="text"
          value={chatInput}
          onChange={(e) => onChangeChatInput(e.target.value)}
          placeholder="Test visitor prompt in sandbox..."
          disabled={isSimulating}
          className="flex-1 text-xs px-3 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 focus:outline-hidden focus:ring-1 focus:ring-indigo-500"
        />
        <button
          type="submit"
          disabled={!chatInput.trim() || isSimulating}
          className="px-3.5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition disabled:opacity-50 flex items-center gap-1"
        >
          {isSimulating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <ArrowRight className="w-3.5 h-3.5" />
          )}
          <span>Send</span>
        </button>
      </form>
    </div>
  );
}
